/**
 * The account-level queues on the team member dashboard, one per tab (TRE-1854).
 *
 * Visibility lives here as a pure function so the rules are readable in one place and testable
 * without a store: a section a viewer cannot see is absent, not empty — an empty tab reads as
 * "nothing to do" and would be a lie for someone who simply has no access to that queue.
 */
export const DashboardSection = {
  CHAT_ALERTS: "chatAlerts",
  UNASSIGNED_CLIENTS: "unassignedClients",
  CONSULTATIONS: "consultations",
  CLIENTS_WITHOUT_PLAN: "clientsWithoutPlan",
  ASSIGNED_TRAINERS_TASKS: "assignedTrainersTasks",
} as const;

export type DashboardSectionId = (typeof DashboardSection)[keyof typeof DashboardSection];

export interface DashboardViewer {
  /**
   * ADMINISTRATION — works the whole account, but does not run consultations
   * (TRE-1841). The only role condition left here on purpose: the catalogue has
   * no key for "I run consultations", and clients.write ASSIGNED means something
   * else. Checked during the TRE-1855 sweep and kept deliberately.
   */
  isAdministration: boolean;
  /** `clients.read` in any scope — SUPPORT works tickets and simply has none. */
  canReadClients: boolean;
  /**
   * Oversees at least one trainer, per `team_member_assigned` — which is what
   * `useAssignedTrainersTasks` queries on. Read off the `clients.read` scope
   * (=== TEAM) until TRE-2212, so a head coach whose scope was widened to ALL
   * silently lost the tab although the assignments were still there.
   */
  coachesATeam: boolean;
  /** `clients.write` at ALL scope — assigning a trainer is an account-level action. */
  canAssignTrainers: boolean;
  /** `chat.alerts.read`, granted per person in the permissions drawer rather than by role (TRE-1850). */
  canReadChatAlerts: boolean;
}

const sectionVisibility: Record<DashboardSectionId, (viewer: DashboardViewer) => boolean> = {
  // The permission is the only gate — no role condition rides along with it (TRE-1850).
  [DashboardSection.CHAT_ALERTS]: ({ canReadChatAlerts }) => canReadChatAlerts,
  // A full-access client nobody is coaching is an account-level task: no coach has that client on
  // their list to notice it (TRE-1848).
  [DashboardSection.UNASSIGNED_CLIENTS]: ({ canReadClients, canAssignTrainers }) => canReadClients && canAssignTrainers,
  [DashboardSection.CONSULTATIONS]: ({ canReadClients, isAdministration }) => canReadClients && !isAdministration,
  [DashboardSection.CLIENTS_WITHOUT_PLAN]: ({ canReadClients }) => canReadClients,
  [DashboardSection.ASSIGNED_TRAINERS_TASKS]: ({ canReadClients, coachesATeam }) => canReadClients && coachesATeam,
};

/** Tab order: the queues that need a decision first, the informational schedule in the middle. */
const sectionOrder: DashboardSectionId[] = [
  DashboardSection.CHAT_ALERTS,
  DashboardSection.UNASSIGNED_CLIENTS,
  DashboardSection.CONSULTATIONS,
  DashboardSection.CLIENTS_WITHOUT_PLAN,
  DashboardSection.ASSIGNED_TRAINERS_TASKS,
];

export const dashboardSectionLabels: Record<DashboardSectionId, string> = {
  [DashboardSection.CHAT_ALERTS]: "Zastrzeżenia klientów do weryfikacji",
  [DashboardSection.UNASSIGNED_CLIENTS]: "Klienci bez przypisanego trenera",
  [DashboardSection.CONSULTATIONS]: "Zaplanowane konsultacje",
  [DashboardSection.CLIENTS_WITHOUT_PLAN]: "Klienci bez planu lub diety",
  [DashboardSection.ASSIGNED_TRAINERS_TASKS]: "Zadania twoich trenerów",
};

/**
 * A count that means "somebody has to act on this" is worth flagging; the weekly schedule is just
 * information and would cry wolf in red.
 */
export const dashboardSectionBadgeTone: Record<DashboardSectionId, "alert" | "neutral"> = {
  [DashboardSection.CHAT_ALERTS]: "alert",
  [DashboardSection.UNASSIGNED_CLIENTS]: "alert",
  [DashboardSection.CONSULTATIONS]: "neutral",
  [DashboardSection.CLIENTS_WITHOUT_PLAN]: "alert",
  [DashboardSection.ASSIGNED_TRAINERS_TASKS]: "alert",
};

export const getVisibleDashboardSections = (viewer: DashboardViewer): DashboardSectionId[] =>
  sectionOrder.filter((section) => sectionVisibility[section](viewer));

export const isDashboardSectionId = (value: string | null): value is DashboardSectionId =>
  value !== null && sectionOrder.includes(value as DashboardSectionId);
